import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Paper,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { endOfMonth, format, startOfMonth, subDays, subMonths } from "date-fns";
import type React from "react";
import { useState } from "react";
import { strings } from "../../I18n";

interface DateRange {
  startDate: Date;
  endDate: Date;
  key: string;
}

interface Props {
  dateRange: DateRange;
  onChange: (range: DateRange) => void;
}

const CompactDateRangePicker: React.FC<Props> = ({ dateRange, onChange }) => {
  const presetRanges = [
    {
      label: strings("descriptions.last7Days"),
      getValue: () => ({ start: subDays(new Date(), 7), end: new Date() }),
    },
    {
      label: strings("descriptions.last30Days"),
      getValue: () => ({ start: subDays(new Date(), 30), end: new Date() }),
    },
    {
      label: strings("descriptions.last90Days"),
      getValue: () => ({ start: subDays(new Date(), 90), end: new Date() }),
    },
    {
      label: strings("descriptions.thisMonth"),
      getValue: () => ({
        start: startOfMonth(new Date()),
        end: endOfMonth(new Date()),
      }),
    },
    {
      label: strings("descriptions.lastMonth"),
      getValue: () => {
        const lastMonth = subMonths(new Date(), 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      },
    },
  ];

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(
    dateRange.startDate
  );
  const [tempEndDate, setTempEndDate] = useState<Date | null>(
    dateRange.endDate
  );

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setTempStartDate(dateRange.startDate);
    setTempEndDate(dateRange.endDate);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onChange({
        startDate: tempStartDate,
        endDate: tempEndDate,
        key: "selection",
      });
    }
    handleClose();
  };

  const handlePresetClick = (preset: (typeof presetRanges)[0]) => {
    const { start, end } = preset.getValue();
    setTempStartDate(start);
    setTempEndDate(end);
    onChange({
      startDate: start,
      endDate: end,
      key: "selection",
    });
    handleClose();
  };

  const formatDateRange = () => {
    if (!dateRange.startDate || !dateRange.endDate)
      return strings("descriptions.selectDateRangePlaceholder");
    return `${format(dateRange.startDate, "MMM d, yyyy")} - ${format(dateRange.endDate, "MMM d, yyyy")}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Chip
          icon={<CalendarTodayIcon />}
          label={formatDateRange()}
          onClick={handleClick}
          color="primary"
          variant="outlined"
          sx={{
            height: 40,
            fontSize: "0.9rem",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "primary.50",
            },
          }}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          sx={{
            mt: 1,
          }}
        >
          <Paper sx={{ p: 3, minWidth: 500 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {strings("descriptions.selectDateRangeTitle")}
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {strings("descriptions.quickSelect")}
              </Typography>
              <ButtonGroup
                variant="outlined"
                size="small"
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {presetRanges.map((preset) => (
                  <Button
                    key={preset.label}
                    onClick={() => handlePresetClick(preset)}
                    sx={{ textTransform: "none" }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Stack>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {strings("descriptions.customRange")}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <DatePicker
                  label={strings("descriptions.startDate")}
                  value={tempStartDate}
                  onChange={(date) => setTempStartDate(date)}
                  maxDate={tempEndDate || new Date()}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
                <Typography color="text.secondary">
                  {strings("descriptions.to")}
                </Typography>
                <DatePicker
                  label={strings("descriptions.endDate")}
                  value={tempEndDate}
                  onChange={(date) => setTempEndDate(date)}
                  minDate={tempStartDate || undefined}
                  maxDate={new Date()}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={handleClose} variant="outlined" size="small">
                {strings("buttons.cancel")}
              </Button>
              <Button
                onClick={handleApply}
                variant="contained"
                size="small"
                disabled={!tempStartDate || !tempEndDate}
              >
                {strings("buttons.apply")}
              </Button>
            </Stack>
          </Paper>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default CompactDateRangePicker;
